import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { Button, Grid, CircularProgress, Typography } from '@mui/material';

import theme from '../theme';
import CustomTextField from './textfield';
import CustomSelect from './customSelect';
import { Colors } from '../config/default';
import { addRole, changeUserRole, getUserInfo } from '../services/services';
import { useToast } from '../toast/toastContext';
import { isEmailValid } from '../services/validationService';
import { useSelector, useDispatch } from 'react-redux';
import { userRoles } from '../constants/appConstants';
import { user_information } from '../redux/actions/action';
import { clearSessionPreservingTab } from '../utils/tabSession';

function AddUser({ getUsers, setOpen, show, data }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [role, setRole] = useState(data?.role || '');
  const [email, setEmail] = useState(data?.email || '');
  const [name, setName] = useState(data?.name || '');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { CLEVEL, OWNER, SUPPORT } = userRoles;
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const user = useSelector((state) => state.signIn?.signIn?.cognitoRes?.idToken?.payload);
  const cognitoUsername = useSelector(
    (state) => state?.signIn?.signIn?.cognitoRes?.accessToken?.payload?.username
  );
  const companyId = userDetails?.company?._id;
  const normalizeRole = (role) => role?.replace(/_/g, ' ');
  const loggedInUserRole = normalizeRole(role);

  const companyRoles = (userDetails?.company?.companyRoles || []).filter((role) => {
    const normalizedRole = normalizeRole(role);
    return normalizedRole !== OWNER && normalizedRole !== loggedInUserRole;
  });

  const createNewUser = async () => {
    setLoading(true);
    const params = {
      email: email?.toLowerCase(),
      role: role === 'C Level' ? CLEVEL : role,
      name: name,
      erpUser: data?.erpUser || false,
      subAdmin: role === SUPPORT
    };
    const result = await addRole(params);
    if (result?.status === 200) {
      showToast(result?.data?.metadata?.message, 'success');
      setOpen(false);
      getUsers();
    } else if (result?.response?.status === 401) {
      clearSessionPreservingTab();
      navigate('/');
    } else {
      showToast(
        result?.data?.metadata?.message || result?.response?.data?.metadata?.message,
        'error'
      );
    }

    setLoading(false);
  };
  const handleClose = (event) => {
    const selectedValue = event.target.getAttribute('data-value');
    setRole(selectedValue);
    setAnchorEl(null);
  };

  const handleChangeUserRole = async () => {
    setLoading(true);
    const newRole = role === 'C Level' ? 'C_Level' : role;
    const params = {
      email: email?.toLowerCase(),
      role: newRole,
      signup: data?.isSignUp === 'Completed' ? true : false,
      companyId: companyId,
      subAdmin: newRole === SUPPORT
    };

    const response = await changeUserRole(params);

    if (response?.status === 200) {
      showToast(response?.data?.metadata?.message, 'success');
      setOpen(false);
      getUsers();
      const getUserresult = await getUserInfo(cognitoUsername);
      if (getUserresult?.status === 200) {
        dispatch(user_information(getUserresult?.data?.payload?.data));
        if (
          !getUserresult?.data?.payload?.data?.roleConfigurations?.rolePermissions?.[newRole]
            ?.updateUsers &&
          email?.toLowerCase() === user?.email?.toLowerCase()
        ) {
          navigate('/home');
        }
      }
    } else if (response?.response?.status === 401) {
      clearSessionPreservingTab();
      navigate('/');
    } else {
      showToast(
        response?.data?.metadata?.message || response?.response?.data?.metadata?.message,
        'error'
      );
    }

    setLoading(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CustomTextField
        disabled={show === 'Edit_Users'}
        toShow={show}
        setValue={setEmail}
        value={email}
        placeHolder="Email"
      />
      <CustomTextField
        disabled={data}
        setValue={setName}
        value={name}
        placeHolder="Designation (visible to user)"
        mt="1rem"
      />
      <CustomSelect
        disabled={data && show !== 'Edit_Users'}
        text="Add New Role"
        role={role}
        handleClose={handleClose}
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        options={companyRoles}
        setRole={setRole}
      />
      {show === 'Edit_Users' && data?.isSignUp === 'Pending' && (
        <Typography
          sx={{
            color: Colors.MUSTARD,
            fontSize: '.8em',
            textAlign: 'center',
            mt: '.5rem'
          }}>
          Changing the role does not email the user. Use Resend on the Users
          page to send the invitation again.
        </Typography>
      )}
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '1em'
        }}>
        <Button
          disabled={
            role === '' ||
            role === null ||
            email === '' ||
            loading ||
            !isEmailValid(email) ||
            name === '' ||
            (role === data?.role && show === 'Edit_Users')
          }
          variant="contained"
          onClick={show === 'Edit_Users' ? handleChangeUserRole : createNewUser}
          sx={{
            borderRadius: '10px',
            backgroundColor: Colors.DEEPBLUE,
            color: '#fff',
            width: '6rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: Colors.DEEPBLUE
            },
            //Explicit disabled styles: the hard-coded color above would
            //otherwise override MUI's dimmed disabled look, making the
            //button appear clickable when it is not.
            '&.Mui-disabled': {
              backgroundColor: Colors.DEEPGREY,
              color: Colors.DISABLEDGRAY
            }
          }}>
          {loading ? <CircularProgress size={25} color="inherit" /> : 'Submit'}
        </Button>
      </Grid>
    </ThemeProvider>
  );
}

export default AddUser;
